import {
  BillingInterval,
  Currency,
  SubscriptionTier,
} from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Dialog, Label, Select, SelectItem, toast } from '@utima/ui';
import { useEffect, useState } from 'react';

import { invalidateCurrentOrganization } from '@/services/invalidateCurrentOrganization';
import { trpc } from '@/services/trpc';

interface ChangeCurrencyAndPlanDialogProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  currentCurrency: Currency;
  currentTier?: SubscriptionTier; // Made optional, will be fetched if not provided or for display
  currentInterval?: BillingInterval; // Made optional
  onSuccess: () => void; // Callback after successful change
}

export function ChangeCurrencyAndPlanDialog({
  isOpen,
  onClose,
  organizationId,
  currentCurrency,
  currentTier: initialCurrentTier,
  currentInterval: initialCurrentInterval,
  onSuccess,
}: ChangeCurrencyAndPlanDialogProps) {
  const { t } = useLingui();
  const utils = trpc.useUtils();

  // State for selections inside the dialog
  const [targetCurrency, setTargetCurrency] = useState<Currency | ''>(() =>
    currentCurrency === Currency.USD ? Currency.CZK : Currency.USD,
  );
  const [targetTier, setTargetTier] = useState<SubscriptionTier | ''>(
    initialCurrentTier || '',
  );
  const [targetInterval, setTargetInterval] = useState<BillingInterval>(
    initialCurrentInterval || BillingInterval.Month,
  );
  const [changePlanToo, setChangePlanToo] = useState(false);

  // Fetch active subscription to get current tier and interval if not passed or to ensure freshness
  const { data: activeSubscription, isLoading: isLoadingActiveSubscription } =
    trpc.subscriptions.getActive.useQuery(
      { organizationId },
      {
        enabled: isOpen, // Only fetch when dialog is open
        staleTime: 5 * 60 * 1000, // Stale after 5 minutes
        refetchOnWindowFocus: true,
      },
    );

  const effectiveCurrentTier =
    activeSubscription?.tier ?? initialCurrentTier ?? SubscriptionTier.Free;
  const effectiveCurrentInterval =
    activeSubscription?.billingInterval ??
    initialCurrentInterval ??
    BillingInterval.Month;

  // Fetch tier details to populate dropdown
  const { data: tierDetails, isLoading: isLoadingTiers } =
    trpc.subscriptions.getTierDetails.useQuery(undefined, { enabled: isOpen });

  const changeMutation = trpc.subscriptions.changeCurrencyAndPlan.useMutation({
    onSuccess: () => {
      toast.success(
        t`Super Admin: Org currency${changePlanToo ? ' and plan' : ''} changed successfully!`,
      );
      utils.organizations.findOne.invalidate({ id: organizationId });
      void invalidateCurrentOrganization(organizationId);
      utils.subscriptions.getActive.invalidate({ organizationId });
      utils.subscriptions.getTierDetails.invalidate();
      onSuccess();
      onClose();
    },
    onError: error => {
      toast.error(
        t`Super Admin: Failed to change currency${changePlanToo ? ' and plan' : ''}: ${error.message}`,
      );
      console.error('Super Admin Change Currency/Plan Error:', error);
    },
  });

  // Effect to set initial targetTier and targetInterval when activeSubscription loads or changePlanToo changes
  useEffect(() => {
    if (!changePlanToo && activeSubscription) {
      setTargetTier(activeSubscription.tier ?? SubscriptionTier.Free);
      setTargetInterval(
        activeSubscription.billingInterval || BillingInterval.Month,
      );
    } else if (changePlanToo) {
      // If user wants to change plan, clear previous auto-filled plan, let them select
      setTargetTier('');
    }
    // Reset targetTier if dialog is closed and reopened and changePlanToo is false
    if (!isOpen && !changePlanToo) {
      setTargetTier(initialCurrentTier || '');
      setTargetInterval(initialCurrentInterval || BillingInterval.Month);
    }
  }, [
    activeSubscription,
    changePlanToo,
    isOpen,
    initialCurrentTier,
    initialCurrentInterval,
  ]);

  const handleSubmit = () => {
    if (!targetCurrency) {
      toast.warning(t`Please select a target currency.`);

      return;
    }

    if (changePlanToo && !targetTier) {
      toast.warning(t`Please select a target plan.`);

      return;
    }

    let finalTargetTier: SubscriptionTier;
    let finalTargetInterval: BillingInterval;

    if (changePlanToo && targetTier) {
      finalTargetTier = targetTier;
      finalTargetInterval = targetInterval;
    } else {
      finalTargetTier = activeSubscription?.tier ?? SubscriptionTier.Free;
      finalTargetInterval =
        activeSubscription?.billingInterval || BillingInterval.Month;
    }

    changeMutation.mutate({
      organizationId,
      targetCurrency,
      targetTier: finalTargetTier,
      targetBillingInterval: finalTargetInterval,
    });
  };

  const availableTiers =
    tierDetails?.filter(tier => tier.id !== SubscriptionTier.Enterprise) || [];

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Content className='md:max-w-lg'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Super Admin: Change Organization Currency & Plan</Trans>
          </Dialog.Title>
          <Dialog.Description className='mt-2'>
            <Trans>
              You are performing a Super Admin override. This will directly
              modify the Stripe subscription and billing currency for the
              organization.
              <br />
              Current Billing Currency:{' '}
              <strong>{currentCurrency.toUpperCase()}</strong>
              <br />
              Current Plan:{' '}
              <strong>
                {effectiveCurrentTier} ({effectiveCurrentInterval})
              </strong>
            </Trans>
          </Dialog.Description>
        </Dialog.Header>

        <div className='space-y-4 py-4'>
          <div>
            <Label htmlFor='target-currency'>
              <Trans>Select New Billing Currency</Trans>
            </Label>
            <Select
              id='target-currency'
              value={targetCurrency}
              onValueChange={value => setTargetCurrency(value as Currency)}
              placeholder={t`Select currency...`}
            >
              {Object.values(Currency).map(currency => (
                <SelectItem
                  key={currency}
                  value={currency}
                  disabled={currency === currentCurrency}
                >
                  {currency.toUpperCase()}
                </SelectItem>
              ))}
            </Select>
          </div>

          <div className='flex items-center space-x-2 pt-2'>
            <input
              type='checkbox'
              id='change-plan-too'
              checked={changePlanToo}
              onChange={e => setChangePlanToo(e.target.checked)}
              className='size-4 rounded border-gray-300 text-primary focus:ring-primary'
            />
            <label htmlFor='change-plan-too' className='text-sm'>
              <Trans>Also change the subscription plan</Trans>
            </label>
          </div>

          {/* Informational text based on changePlanToo */}
          {!changePlanToo && activeSubscription && (
            <p className='text-xs text-gray-500'>
              <Trans>
                The organization will remain on their current plan (
                <strong>
                  {activeSubscription.tier}{' '}
                  {activeSubscription.billingInterval || BillingInterval.Month}
                </strong>
                ), but will be billed in{' '}
                <strong>{targetCurrency || 'the new currency'}</strong>.
              </Trans>
            </p>
          )}

          {changePlanToo && (
            <>
              <div>
                <Label htmlFor='target-tier'>
                  <Trans>Select New Target Plan</Trans>
                </Label>
                <Select
                  id='target-tier'
                  value={targetTier}
                  onValueChange={value =>
                    setTargetTier(value as SubscriptionTier)
                  }
                  placeholder={t`Select plan...`}
                  disabled={
                    isLoadingActiveSubscription ||
                    isLoadingTiers ||
                    !changePlanToo
                  }
                >
                  {isLoadingTiers || isLoadingActiveSubscription ? (
                    <SelectItem value='loading' disabled>
                      {t`Loading plans...`}
                    </SelectItem>
                  ) : (
                    availableTiers.map(tier => (
                      <SelectItem
                        key={tier.id}
                        value={tier.id}
                        disabled={
                          (tier.id === SubscriptionTier.Free &&
                            effectiveCurrentTier !== SubscriptionTier.Free) ||
                          !changePlanToo
                        }
                      >
                        {tier.name}
                      </SelectItem>
                    ))
                  )}
                </Select>
              </div>

              {targetTier && targetTier !== SubscriptionTier.Free && (
                <div>
                  <Label htmlFor='target-interval'>
                    <Trans>Select New Billing Interval</Trans>
                  </Label>
                  <Select
                    id='target-interval'
                    value={targetInterval}
                    onValueChange={value =>
                      setTargetInterval(value as BillingInterval)
                    }
                    placeholder={t`Select interval...`}
                    disabled={isLoadingActiveSubscription || !changePlanToo}
                  >
                    <SelectItem
                      value={BillingInterval.Month}
                    >{t`Monthly`}</SelectItem>
                  </Select>
                </div>
              )}
            </>
          )}

          <p className='pt-2 text-xs text-gray-500'>
            <Trans>
              <strong>Important:</strong> Any existing balance or usage
              allowance will be handled by the backend conversion logic when the
              subscription changes on Stripe.
              {changePlanToo && targetTier
                ? ` The organization will be moved to ${targetTier} and billed in ${targetCurrency || 'new currency'}.`
                : ''}
            </Trans>
          </p>
        </div>

        <Dialog.Footer>
          <Button
            data-cta='admin-change-currency-and-plan-cancel-button'
            variant='ghost'
            onClick={onClose}
            disabled={changeMutation.isPending}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            data-cta='admin-change-currency-and-plan-confirm-button'
            onClick={handleSubmit}
            disabled={
              changeMutation.isPending ||
              !targetCurrency ||
              (changePlanToo && !targetTier) ||
              isLoadingActiveSubscription
            }
            loading={changeMutation.isPending || isLoadingActiveSubscription}
          >
            <Trans>Confirm & Apply Changes</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
